<template>
    <app-layout>
        <template #title>Reservations</template>
        <template #description>Browse all reservations</template>

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- Filters + Search -->
            <div class="mb-4 flex items-center justify-between">
                <div class="gap-2 flex flex-wrap items-center">
                    <button
                        @click="applyStatus(null)"
                        :class="[
                            'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                            !currentStatus
                                ? 'bg-iron-900 text-white'
                                : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                        ]"
                    >
                        All
                    </button>
                    <button
                        v-for="status in statuses"
                        :key="status"
                        @click="applyStatus(status)"
                        :class="[
                            'px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                            currentStatus === status
                                ? 'bg-iron-900 text-white'
                                : 'text-iron-500 hover:text-iron-700 hover:bg-iron-100',
                        ]"
                    >
                        {{ formatStatusLabel(status) }}
                    </button>
                </div>

                <div class="w-64 relative">
                    <Input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search email, name..."
                        class="pl-8 text-sm"
                        @keydown.enter="applySearch"
                    />
                    <span class="left-2.5 absolute top-1/2 -translate-y-1/2">
                        <Search class="h-3.5 w-3.5 text-iron-400" />
                    </span>
                </div>
            </div>

            <!-- Deferred wrapper -->
            <Deferred data="reservations">
                <template #fallback>
                    <div class="mb-2">
                        <div
                            class="h-4 w-24 bg-iron-100 rounded animate-pulse"
                        ></div>
                    </div>
                    <div
                        class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                    >
                        <div class="p-6">
                            <div class="space-y-3">
                                <div
                                    v-for="i in 10"
                                    :key="i"
                                    class="h-10 bg-iron-100 rounded animate-pulse"
                                ></div>
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Results count -->
                <div class="mb-2 flex items-center justify-between">
                    <p
                        class="font-medium text-iron-400 tracking-wider text-[11px] uppercase tabular-nums"
                    >
                        {{ reservations.reservations.total }} reservations
                    </p>
                    <p
                        v-if="reservations.reservations.last_page > 1"
                        class="font-medium text-iron-400 tracking-wider text-[11px] tabular-nums"
                    >
                        Page {{ reservations.reservations.current_page }} of
                        {{ reservations.reservations.last_page }}
                    </p>
                </div>

                <!-- Reservations Table -->
                <div
                    class="bg-white rounded-xl border-iron-200 overflow-hidden border"
                >
                    <div>
                        <table class="w-full table-fixed">
                            <colgroup>
                                <col class="w-[20%]" />
                                <col class="w-[16%]" />
                                <col class="w-[13%]" />
                                <col class="w-[9%]" />
                                <col class="w-[16%]" />
                                <col class="w-[16%]" />
                                <col class="w-[10%]" />
                            </colgroup>
                            <thead>
                                <tr class="border-iron-100 border-b">
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                    >
                                        Customer
                                    </th>
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                    >
                                        Event Type
                                    </th>
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                    >
                                        Team
                                    </th>
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-center text-[11px] uppercase"
                                    >
                                        Status
                                    </th>
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                    >
                                        Start Time
                                    </th>
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-left text-[11px] uppercase"
                                    >
                                        Created
                                    </th>
                                    <th
                                        class="px-5 py-2.5 font-medium text-iron-400 tracking-wider text-right text-[11px] uppercase"
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-iron-50 divide-y">
                                <tr
                                    v-for="reservation in reservations
                                        .reservations.data"
                                    :key="reservation.id"
                                    class="hover:bg-iron-50/70 transition-colors"
                                >
                                    <td class="px-5 py-2.5">
                                        <TooltipProvider :delay-duration="300">
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <div class="min-w-0">
                                                        <div
                                                            v-if="
                                                                reservation.name?.trim()
                                                            "
                                                            class="text-sm font-medium text-iron-900 truncate"
                                                        >
                                                            {{
                                                                reservation.name
                                                            }}
                                                        </div>
                                                        <div
                                                            class="text-iron-400 font-mono truncate text-[13px]"
                                                        >
                                                            {{
                                                                reservation.email
                                                            }}
                                                        </div>
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom">
                                                    <p
                                                        v-if="
                                                            reservation.name?.trim()
                                                        "
                                                        class="font-medium"
                                                    >
                                                        {{ reservation.name }}
                                                    </p>
                                                    <p>
                                                        {{ reservation.email }}
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </td>
                                    <td class="px-5 py-2.5">
                                        <TooltipProvider :delay-duration="300">
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <div
                                                        class="text-sm text-iron-600 truncate"
                                                    >
                                                        {{
                                                            reservation.event_type_name ||
                                                            "—"
                                                        }}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    v-if="
                                                        reservation.event_type_name
                                                    "
                                                    side="bottom"
                                                >
                                                    {{
                                                        reservation.event_type_name
                                                    }}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </td>
                                    <td class="px-5 py-2.5">
                                        <TooltipProvider :delay-duration="300">
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <div
                                                        class="text-sm text-iron-600 truncate"
                                                    >
                                                        {{
                                                            reservation.team_name ||
                                                            "—"
                                                        }}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    v-if="reservation.team_name"
                                                    side="bottom"
                                                >
                                                    {{ reservation.team_name }}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </td>
                                    <td
                                        class="px-5 py-2.5 text-center whitespace-nowrap"
                                    >
                                        <button
                                            @click="
                                                applyStatus(reservation.status)
                                            "
                                        >
                                            <span
                                                :class="
                                                    statusClass(
                                                        reservation.status,
                                                    )
                                                "
                                                class="font-semibold px-2 py-0.5 inline-block rounded-full text-[11px]"
                                            >
                                                {{
                                                    formatStatusLabel(
                                                        reservation.status,
                                                    )
                                                }}
                                            </span>
                                        </button>
                                    </td>
                                    <td
                                        class="px-5 py-2.5 text-sm text-iron-600 font-mono whitespace-nowrap tabular-nums"
                                    >
                                        {{ formatDate(reservation.start) }}
                                    </td>
                                    <td
                                        class="px-5 py-2.5 text-sm text-iron-400 font-mono whitespace-nowrap tabular-nums"
                                    >
                                        {{ formatDate(reservation.created_at) }}
                                    </td>
                                    <td class="px-5 py-2.5 text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger as-child>
                                                <button
                                                    class="rounded-md p-1 hover:bg-iron-100 inline-flex items-center justify-center transition-colors"
                                                >
                                                    <EllipsisVertical
                                                        class="h-4 w-4 text-iron-400"
                                                    />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                    :disabled="
                                                        reservation.integration_event_id !==
                                                        null
                                                    "
                                                    @click="
                                                        retryIntegration(
                                                            reservation,
                                                        )
                                                    "
                                                    class="gap-2"
                                                >
                                                    <RefreshCw
                                                        class="h-4 w-4"
                                                    />
                                                    Retry Integration
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Empty state -->
                    <div
                        v-if="reservations.reservations.data.length === 0"
                        class="py-16 text-center"
                    >
                        <div
                            class="w-10 h-10 rounded-lg bg-iron-100 mb-3 mx-auto flex items-center justify-center"
                        >
                            <CalendarCheck class="h-4 w-4 text-iron-400" />
                        </div>
                        <p class="text-sm text-iron-500">
                            No reservations match your filters.
                        </p>
                    </div>

                    <!-- Pagination -->
                    <div
                        v-if="reservations.reservations.last_page > 1"
                        class="border-iron-100 px-5 py-3 flex items-center justify-between border-t"
                    >
                        <span class="text-iron-400 text-[11px] tabular-nums">
                            Page
                            {{ reservations.reservations.current_page }} of
                            {{ reservations.reservations.last_page }}
                            <span class="text-iron-300 ml-1"
                                >({{
                                    reservations.reservations.total
                                }}
                                total)</span
                            >
                        </span>
                        <div class="gap-1.5 flex">
                            <inertia-link
                                v-if="reservations.reservations.prev_page_url"
                                :href="reservations.reservations.prev_page_url"
                                preserve-state
                                class="rounded-md border-iron-200 px-2.5 py-1 font-medium text-iron-500 hover:bg-iron-50 border text-[11px] transition-colors"
                            >
                                Previous
                            </inertia-link>
                            <inertia-link
                                v-if="reservations.reservations.next_page_url"
                                :href="reservations.reservations.next_page_url"
                                preserve-state
                                class="rounded-md border-iron-200 px-2.5 py-1 font-medium text-iron-500 hover:bg-iron-50 border text-[11px] transition-colors"
                            >
                                Next
                            </inertia-link>
                        </div>
                    </div>
                </div>
            </Deferred>
        </div>
    </app-layout>
</template>

<script setup lang="ts">
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import route from "ziggy-js";
import { Input } from "../../../../components/ui/input";
import { ref, computed } from "vue";
import {
    Search,
    CalendarCheck,
    EllipsisVertical,
    RefreshCw,
} from "lucide-vue-next";
import { Deferred, router } from "@inertiajs/vue3";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../../../components/ui/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";

type ReservationItem = {
    id: number;
    uuid: string;
    email: string;
    name: string;
    phone: string | null;
    status: string;
    start: string | null;
    end: string | null;
    created_at: string | null;
    event_type_name: string | null;
    team_name: string | null;
    organizer_name: string | null;
    integration_event_id: string | null;
};

type PaginatedReservations = {
    data: ReservationItem[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    next_page_url: string | null;
    prev_page_url: string | null;
};

type Filters = {
    search: string | null;
    status: string | null;
    date_from: string | null;
    date_to: string | null;
    per_page: number;
};

const props = defineProps<{
    filters: Filters;
    reservations: {
        reservations: PaginatedReservations;
    };
    statuses: string[];
}>();

const currentStatus = computed(() => props.filters.status);
const searchQuery = ref(props.filters.search ?? "");

function formatDate(dateString: string | null): string {
    if (!dateString) return "—";
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString;
        return d.toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return dateString;
    }
}

function formatStatusLabel(status: string): string {
    return status
        .split(/[\s_-]+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

function statusClass(status: string): string {
    const map: Record<string, string> = {
        synced: "bg-emerald-50 text-emerald-600",
        confirmed: "bg-emerald-50 text-emerald-600",
        canceled: "bg-red-50 text-red-600",
        failed: "bg-red-50 text-red-600",
        expired: "bg-iron-100 text-iron-500",
        created: "bg-amber-50 text-amber-600",
        "waiting for confirmation": "bg-amber-50 text-amber-600",
        syncing: "bg-blue-50 text-blue-600",
    };
    return map[status] ?? "bg-iron-100 text-iron-500";
}

function buildParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (currentStatus.value) {
        params.status = currentStatus.value;
    }
    if (searchQuery.value) {
        params.search = searchQuery.value;
    }
    return params;
}

function applyStatus(status: string | null) {
    const params: Record<string, string> = {};
    if (status) {
        params.status = status;
    }
    if (searchQuery.value) {
        params.search = searchQuery.value;
    }
    router.get(route("app.kiosk.reservations.index"), params, {
        preserveState: true,
    });
}

function applySearch() {
    router.get(route("app.kiosk.reservations.index"), buildParams(), {
        preserveState: true,
    });
}

function retryIntegration(reservation: ReservationItem) {
    router.post(
        route("app.kiosk.reservations.retry-integration", reservation.id),
        {},
        { preserveState: true },
    );
}
</script>
